/**
 * Jetons de couleur des deux thèmes.
 *
 * Déclarés en TypeScript et non directement en CSS pour une raison précise :
 * ils sont ainsi **vérifiables par un test**. La promesse « contraste WCAG AA
 * sur les deux thèmes » ne tient pas si personne ne la mesure — et personne ne
 * la mesure à l'œil.
 *
 * `generateThemeCss()` produit le CSS à partir de cette source unique : aucune
 * valeur ne peut diverger entre ce qui est testé et ce qui est servi.
 *
 * ── Pourquoi deux, et non quatre ────────────────────────────────────────────
 * « Corporate » n'était qu'un clair plus contrasté : il doublait le coût de
 * chaque décision de couleur sans apporter d'identité. « Système » n'était pas
 * un thème mais un défaut — l'application suivait le réglage du poste. Elle
 * ouvre désormais toujours en clair, et le choix de l'utilisateur, une fois
 * fait, est mémorisé par poste de travail.
 */

export type NomTheme = 'light' | 'dark';

/**
 * Un jeton par usage, pas par couleur.
 *
 * `--surface` plutôt que `--gris-50` : le nom dit à quoi il sert, ce qui
 * permet de le réassigner dans un thème sombre sans que son nom devienne un
 * mensonge.
 *
 * ── Deux thèmes, un seul langage ────────────────────────────────────────────
 * Les rôles ci-dessous sont les mêmes des deux côtés et portent les mêmes noms.
 * Ce qui change d'un thème à l'autre, ce sont les valeurs — jamais la
 * grammaire. C'est cette contrainte qui empêche l'un des deux de dériver.
 */
export interface JetonsTheme {
  /** Fond de page. */
  bg: string;
  /**
   * Fond secondaire : barre latérale, rails, zones de service.
   *
   * En clair il est plus sombre que la page, en sombre plus profond : dans les
   * deux cas il RECULE, pour que le contenu avance.
   */
  bgSubtle: string;
  /** Fond des cartes et panneaux, posé sur `bg`. */
  surface: string;
  /** Fond survolé. État transitoire, tenu par la souris. */
  surfaceHover: string;
  /**
   * Fond SÉLECTIONNÉ — un état, celui-là, qui persiste.
   *
   * Distinct du survol : les confondre faisait qu'une ligne sélectionnée
   * disparaissait dès que la souris s'en allait.
   */
  surfaceActive: string;
  /**
   * Filets décoratifs : séparation visuelle entre deux surfaces.
   *
   * Pas soumis au seuil WCAG 3:1 — ces traits n'apportent aucune information,
   * les retirer ne change pas la compréhension. Ils doivent seulement rester
   * perceptibles.
   */
  border: string;
  /**
   * Contours d'éléments INTERACTIFS : champs de saisie, cases à cocher,
   * boutons secondaires.
   *
   * Soumis au seuil 3:1 (WCAG 1.4.11) : c'est ce trait qui indique où l'on
   * peut écrire. À 1,2:1, une personne malvoyante ne voit tout simplement pas
   * le champ.
   */
  borderStrong: string;
  /** Texte principal. */
  fg: string;
  /** Texte secondaire : libellés, valeurs de second rang. */
  fgMuted: string;
  /**
   * Texte tertiaire : légendes, unités, texte d'aide.
   *
   * Troisième niveau et dernier — un quatrième deviendrait illisible avant
   * d'être utile. Il reste au-dessus de 4,5:1 : discret ne veut pas dire
   * effacé.
   */
  fgSubtle: string;
  /** Couleur d'accent : actions principales, liens, onglet actif, focus. */
  primary: string;
  /** Texte posé sur `primary`. */
  primaryFg: string;
  /** Variante survolée de l'accent. */
  primaryHover: string;
  /** Variante pressée : un bouton doit répondre au doigt, pas qu'au survol. */
  primaryActive: string;
  /** États. */
  success: string;
  warning: string;
  danger: string;
  dangerFg: string;
  /**
   * Variante survolee du bouton destructif.
   *
   * Il empruntait `opacity-90`, qui laisse transparaitre le fond : le meme
   * bouton n'avait donc pas la meme teinte sur une carte et sur un tableau.
   */
  dangerHover: string;
  /**
   * Information : ce qui n'est ni bon ni mauvais mais mérite d'être lu.
   *
   * Sans ce jeton, les messages neutres empruntaient l'accent — et prenaient
   * l'apparence d'une action à faire.
   */
  info: string;
  /** Anneau de focus clavier. */
  focus: string;

  /**
   * Fond des en-têtes de tableau et barres d'outils.
   *
   * Distinct de `surfaceHover` : celui-ci est un état transitoire, celui-là une
   * zone permanente. Les confondre fait qu'un en-tête de tableau paraît
   * survolé en permanence.
   */
  surfaceRaised: string;
  /**
   * Fond des champs de saisie.
   *
   * Sur le thème clair il est légèrement plus froid que `surface` : c'est ce
   * décalage, plus que le contour, qui fait qu'un champ se repère au premier
   * coup d'œil dans un formulaire dense.
   */
  surfaceInput: string;
  /** Fond des lignes alternées d'un tableau. Très subtil, jamais décoratif. */
  surfaceZebra: string;
  /** Teinte d'accent très diluée : sélection de ligne, onglet actif discret. */
  primarySoft: string;
  /** Texte lisible sur `primarySoft`. */
  primarySoftFg: string;
  /** Fonds diluées des états, pour les bandeaux et étiquettes. */
  successSoft: string;
  warningSoft: string;
  dangerSoft: string;
  infoSoft: string;
  /**
   * Encre des bandeaux dilués.
   *
   * Séparée de la couleur d'état : posée sur un fond déjà teinté, la couleur
   * vive passait tout juste le seuil en clair et éblouissait en sombre. Une
   * encre dédiée laisse régler les deux indépendamment.
   */
  successSoftFg: string;
  warningSoftFg: string;
  dangerSoftFg: string;
  infoSoftFg: string;
  /**
   * Éléments désactivés.
   *
   * Un jeton, et non une opacité. Trois `opacity-*` différentes traînaient dans
   * le code — 40, 50, 60 — donnant trois gris pour un même état ; et une
   * opacité empile ses effets dès que le fond est teinté.
   *
   * WCAG dispense les commandes désactivées de son seuil : elles doivent se
   * lire comme indisponibles. On les garde tout de même au-dessus de 2,5:1,
   * pour qu'on sache ce qu'on ne peut pas faire.
   */
  disabledFg: string;
  disabledSurface: string;
  /**
   * Voile posé derrière une modale.
   *
   * Un seul, pour les dialogues comme pour le tiroir mobile : deux valeurs
   * coexistaient pour le même rôle, `.45` et `.55`.
   */
  overlay: string;
  /**
   * Ombres portées.
   *
   * Déclarées par thème : une ombre noire translucide, correcte sur fond clair,
   * devient invisible sur un thème sombre. On y compense par une ombre plus
   * dense et un filet clair en haut.
   */
  ombreCarte: string;
  ombreFlottante: string;
  /** Ombre courte d'un bouton plein, posée sur sa propre couleur. */
  ombreBouton: string;
}

/**
 * Thème clair — celui du travail quotidien, et celui qui s'ouvre par défaut.
 *
 * Neutres légèrement froids : un gris parfaitement neutre paraît clinique, et
 * deux points de bleu donnent à l'accent un terrain qui l'accueille.
 */
const CLAIR: JetonsTheme = {
  bg: '#f5f7fa',
  bgSubtle: '#eceff4',
  surface: '#ffffff',
  surfaceHover: '#eef1f6',
  surfaceActive: '#e4e9f2',
  border: '#e2e6ec',
  // 3,04:1 sur le fond de page, 3,26:1 sur une carte : le minimum du critère
  // WCAG 1.4.11 pour un contour d'élément interactif. Plus clair, le champ
  // devient invisible pour une personne malvoyante.
  borderStrong: '#868f9e',
  fg: '#121821',
  fgMuted: '#57606e',
  fgSubtle: '#69727f',
  // Indigo plutôt que bleu franc : plus profond, moins « lien hypertexte »,
  // et il tient mieux sur une projection en salle de classe.
  primary: '#2f4bd0',
  primaryFg: '#ffffff',
  primaryHover: '#2439aa',
  primaryActive: '#1d2f8c',
  success: '#0e7a45',
  warning: '#8a5600',
  danger: '#c02d26',
  dangerFg: '#ffffff',
  dangerHover: '#9c241e',
  info: '#1c5fa8',
  focus: '#2f4bd0',
  surfaceRaised: '#f2f4f8',
  surfaceInput: '#ffffff',
  surfaceZebra: '#fafbfc',
  primarySoft: '#e9edfc',
  primarySoftFg: '#2439aa',
  successSoft: '#e7f5ed',
  warningSoft: '#fbf1e0',
  dangerSoft: '#fbeaea',
  infoSoft: '#e6eff9',
  successSoftFg: '#0b6039',
  warningSoftFg: '#6f4500',
  dangerSoftFg: '#9c241e',
  infoSoftFg: '#174e8a',
  disabledFg: '#949dab',
  disabledSurface: '#f2f4f8',
  overlay: 'rgba(12,17,24,.45)',
  // Deux ombres superposées : une très courte pour le contact, une plus
  // diffuse pour l'élévation. Une seule ombre paraît toujours collée ou
  // flottante, jamais posée.
  ombreCarte: '0 1px 2px rgba(18,24,33,.04), 0 1px 3px -1px rgba(18,24,33,.05)',
  ombreFlottante:
    '0 1px 3px rgba(18,24,33,.06), 0 12px 28px -8px rgba(18,24,33,.16), '
    + '0 4px 10px -4px rgba(18,24,33,.08)',
  ombreBouton: '0 1px 2px rgba(18,24,33,.14), inset 0 1px 0 rgba(255,255,255,.14)',
};

/**
 * Thème sombre.
 *
 * Le fond n'est pas noir pur : sur un écran OLED, du texte clair sur noir
 * absolu produit un halo qui fatigue à la lecture prolongée — et un cahier
 * journal se lit longtemps.
 */
const SOMBRE: JetonsTheme = {
  bg: '#0e1319',
  bgSubtle: '#0a0e13',
  surface: '#161c24',
  surfaceHover: '#212a36',
  surfaceActive: '#28323f',
  border: '#28303c',
  borderStrong: '#7d8797',
  fg: '#e7eaf0',
  fgMuted: '#a2abba',
  fgSubtle: '#8b95a4',
  primary: '#8496ff',
  primaryFg: '#0e1319',
  // Sur fond sombre, le survol ÉCLAIRCIT — l'inverse du thème clair.
  // Assombrir un accent déjà posé sur du noir le ferait disparaître.
  primaryHover: '#a0aeff',
  primaryActive: '#b4bfff',
  success: '#4ade8f',
  warning: '#f0b34a',
  danger: '#f87c7c',
  dangerFg: '#0e1319',
  // Sur fond sombre le survol eclaircit, comme pour l'accent.
  dangerHover: '#fb9b9b',
  info: '#66aaf0',
  focus: '#a0aeff',
  surfaceRaised: '#1c232d',
  surfaceInput: '#101620',
  surfaceZebra: '#131920',
  primarySoft: '#1d2540',
  primarySoftFg: '#aab5ff',
  successSoft: '#0f2a1e',
  warningSoft: '#2b2113',
  dangerSoft: '#2d1919',
  infoSoft: '#122333',
  successSoftFg: '#68e6a5',
  warningSoftFg: '#f5c470',
  dangerSoftFg: '#fb9b9b',
  infoSoftFg: '#8cc0f5',
  disabledFg: '#646d7a',
  disabledSurface: '#1a212a',
  overlay: 'rgba(4,7,11,.62)',
  // Une ombre ne se voit pas sur fond sombre : c'est le filet clair du haut
  // qui détache la carte, comme une arête éclairée.
  ombreCarte: '0 1px 0 rgba(255,255,255,.05) inset, 0 1px 2px rgba(0,0,0,.5)',
  ombreFlottante:
    '0 1px 0 rgba(255,255,255,.07) inset, 0 16px 40px -8px rgba(0,0,0,.75), '
    + '0 4px 12px -4px rgba(0,0,0,.5)',
  ombreBouton: '0 1px 2px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.10)',
};

/**
 * Jetons qui ne sont PAS des couleurs hexadécimales.
 *
 * Déclarés explicitement plutôt que devinés : le test qui vérifie que chaque
 * couleur est un hexadécimal à six chiffres a besoin de savoir ce qu'il doit
 * ignorer. Ajouter une ombre sans l'inscrire ici fait échouer ce test — c'est
 * voulu, cela force à choisir.
 *
 * `overlay` en fait partie : un voile doit laisser voir au travers, donc porter
 * une transparence qu'un hexadécimal à six chiffres ne peut pas exprimer.
 */
export const JETONS_NON_COULEUR = [
  'ombreCarte', 'ombreFlottante', 'ombreBouton', 'overlay',
] as const;

/**
 * Jetons qui ne sont pas des couleurs DU TOUT.
 *
 * ── Pourquoi une seconde liste ──────────────────────────────────────────────
 * Deux questions se confondaient sous un seul nom : « est-ce un hexadécimal à
 * six chiffres ? » et « est-ce une couleur ? ». Le voile répond non à la
 * première — il porte une transparence — et oui à la seconde : il sert de fond,
 * donc il lui faut le préfixe `--color-` pour que Tailwind le trouve. Une ombre
 * répond non aux deux.
 *
 * Les distinguer évite de reproduire le défaut que la liste précédente avait
 * justement été écrite pour empêcher.
 */
export const JETONS_OMBRE = ['ombreCarte', 'ombreFlottante', 'ombreBouton'] as const;

export const THEMES: Record<NomTheme, JetonsTheme> = {
  light: CLAIR,
  dark: SOMBRE,
};

export const NOMS_THEMES: NomTheme[] = ['light', 'dark'];

export const LIBELLES_THEMES: Record<NomTheme, string> = {
  light: 'Clair',
  dark: 'Sombre',
};

/** Thème servi tant que l'utilisateur n'a rien choisi. */
export const THEME_PAR_DEFAUT: NomTheme = 'light';

/**
 * Convertit les jetons en déclarations de variables CSS.
 *
 * ── Les ombres ne prennent PAS le préfixe `--color-` ────────────────────────
 * Une ombre n'est pas une couleur : c'est une liste de décalages. Nommée
 * `--color-ombre-carte`, elle se retrouvait dans l'espace de noms que Tailwind
 * v4 réserve aux couleurs, alors que les composants la lisent sous
 * `var(--ombre-carte)`. Les deux noms ne se rencontraient jamais, et
 * `shadow-[var(--ombre-carte)]` se résolvait dans le vide : aucune carte de
 * l'application n'avait d'ombre.
 */
function variables(jetons: JetonsTheme): string {
  return Object.entries(jetons)
    .map(([cle, valeur]) => {
      // camelCase → kebab-case : primaryFg → --color-primary-fg
      const nom = cle.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
      // `overlay` est une couleur au sens de Tailwind, malgré sa transparence :
      // il sert de fond. Seules les ombres sortent de cet espace de noms.
      const prefixe = (JETONS_OMBRE as readonly string[]).includes(cle) ? '' : 'color-';
      return `    --${prefixe}${nom}: ${valeur};`;
    })
    .join('\n');
}

/**
 * Produit le CSS des deux thèmes.
 *
 * ── Le thème clair est le défaut, sans consulter le système ─────────────────
 * `:root` porte le thème clair, et rien ne le remplace tant que l'utilisateur
 * n'a pas choisi. La règle `@media (prefers-color-scheme: dark)` a été retirée
 * : elle faisait ouvrir l'application en sombre sur un poste réglé ainsi, ce
 * qui n'est plus voulu. Le choix explicite passe par `[data-theme]`, et il est
 * mémorisé par poste de travail.
 */
export function generateThemeCss(): string {
  return [
    ':root {',
    variables(THEMES.light),
    '}',
    '',
    ...NOMS_THEMES.map((nom) =>
      [`:root[data-theme='${nom}'] {`, variables(THEMES[nom]), '}', ''].join('\n'),
    ),
  ].join('\n');
}
